// 2026-10-21 — new client-facing verification dashboard. Per explicit
// request: a way for lab staff to browse the REAL self-service GCS tree a
// clinician/patient would actually see (patient_reports/ and
// clinician_reports/ at the bucket root), so a just-uploaded report can be
// verified to have landed in the right clinician folder AND the right
// patient folder — same jobs ReportsDashboard.js already does for the
// lab-internal tree, applied to the self-service one instead, plus a
// retracted-state badge, and full Retract/Restore/Permanently Delete
// actions wired into the preview panel.
//
// UI pattern deliberately mirrors ReportsDashboard.js's own file-explorer
// (breadcrumbs + a wrapping grid of folder/file tiles, one level at a
// time) for visual consistency across both admin GCS-browsing surfaces,
// not a bespoke new look. See docs/reference/LABARIUM_ISSUE_CHECKLIST_
// 20261021.md's PLANNED section for the full build plan.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import {
  Box, Typography, IconButton, CircularProgress, Tooltip, Chip, Button,
  Dialog, DialogContent, Checkbox, FormControlLabel,
} from "@mui/material";
import {
  Folder, PictureAsPdf, Download, Refresh, CloudDone,
  PersonOutline, LocalHospital, NavigateNext, Home, Close, Block,
  Insights, WarningAmber, MenuBook, LibraryBooks, UploadFile, Autorenew,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import AdminPageShell from "./AdminPageShell";
import ConfirmDialog from "./ConfirmDialog";
import { extractErrorDetail } from "./validators";
import { formatEST } from "../dateUtils";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const B = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
}

// ─── Landing — two entry cards ─────────────────────────────────────────────
function RootCard({ icon, title, description, onClick }) {
  return (
    <motion.div whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }} transition={{ type: "spring", stiffness: 380, damping: 24 }}>
      <Box onClick={onClick} sx={{
        p: 4, borderRadius: "18px", cursor: "pointer", width: 280,
        border: "1px solid rgba(255,255,255,0.08)", bgcolor: "rgba(255,255,255,0.03)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center",
        transition: "border-color 0.2s, box-shadow 0.2s",
        "&:hover": { borderColor: B.gold, boxShadow: "0 12px 36px rgba(0,0,0,0.3)" },
      }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "18px",
          background: `linear-gradient(135deg, ${B.navy}, #1a4b9e)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{title}</Typography>
        <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>{description}</Typography>
      </Box>
    </motion.div>
  );
}

// ─── Breadcrumbs ────────────────────────────────────────────────────────────
function Breadcrumbs({ parts, onNavigateRoot, onNavigate }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
      <Box onClick={onNavigateRoot} sx={{
        display: "flex", alignItems: "center", cursor: "pointer",
        color: "rgba(255,255,255,0.45)", "&:hover": { color: "#fff" },
      }}>
        <Home sx={{ fontSize: 16 }} />
      </Box>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          <NavigateNext sx={{ fontSize: 14, color: "rgba(255,255,255,0.45)" }} />
          <Tooltip title={part.replace(/\/$/, "")} enterDelay={400}>
            <Typography onClick={() => onNavigate(i)} sx={{
              fontSize: 13.5, cursor: "pointer", maxWidth: 220,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              color: i === parts.length - 1 ? "#fff" : "rgba(255,255,255,0.45)",
              fontWeight: i === parts.length - 1 ? 700 : 400,
              "&:hover": { color: "#fff" },
            }}>
              {part.replace(/\/$/, "")}
            </Typography>
          </Tooltip>
        </React.Fragment>
      ))}
    </Box>
  );
}

// ─── Folder / File tiles — full names via Tooltip, never silently clipped ──
function FolderTile({ name, onClick }) {
  const label = name.replace(/\/$/, "");
  return (
    <Box onClick={onClick} sx={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
      width: 132, p: 1.5, borderRadius: "12px", cursor: "pointer",
      "&:hover": { bgcolor: "rgba(255,255,255,0.06)" }, transition: "background 0.15s",
    }}>
      <Box sx={{ width: 56, height: 56, borderRadius: "14px", bgcolor: "rgba(255,255,255,0.05)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Folder sx={{ fontSize: 28, color: B.gold }} />
      </Box>
      <Tooltip title={label} enterDelay={400}>
        <Typography sx={{
          fontSize: "0.74rem", color: "rgba(255,255,255,0.85)", textAlign: "center",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
        }}>
          {label}
        </Typography>
      </Tooltip>
    </Box>
  );
}

function FileTile({ file, selected, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      position: "relative",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
      width: 132, p: 1.5, borderRadius: "12px", cursor: "pointer",
      bgcolor: selected ? "rgba(192,168,140,0.16)" : "transparent",
      border: selected ? `1px solid ${B.gold}` : "1px solid transparent",
      "&:hover": { bgcolor: selected ? "rgba(192,168,140,0.2)" : "rgba(255,255,255,0.06)" },
      transition: "all 0.15s",
    }}>
      {file.retracted && (
        <Tooltip title="Retracted — not visible to the patient/clinician">
          <Chip icon={<Block sx={{ fontSize: 13 }} />} label="Retracted" size="small" sx={{
            position: "absolute", top: 2, right: 2, height: 18, fontSize: 10, fontWeight: 700,
            bgcolor: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.35)",
            "& .MuiChip-icon": { color: "#f87171", ml: 0.4 },
          }} />
        </Tooltip>
      )}
      <Box sx={{ width: 56, height: 56, borderRadius: "14px",
        bgcolor: selected ? "rgba(192,168,140,0.18)" : "rgba(255,255,255,0.05)",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <PictureAsPdf sx={{ fontSize: 26, color: selected ? B.gold : "#94a3b8" }} />
      </Box>
      <Tooltip title={file.filename} enterDelay={400}>
        <Typography sx={{
          fontSize: "0.72rem", color: selected ? "#f8e9d6" : "rgba(255,255,255,0.8)",
          fontWeight: selected ? 600 : 400, textAlign: "center",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
        }}>
          {file.filename}
        </Typography>
      </Tooltip>
      <Typography sx={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.3)" }}>
        {formatBytes(file.size_bytes)}
      </Typography>
    </Box>
  );
}

// ─── Preview panel ──────────────────────────────────────────────────────────
function PreviewPanel({ file, onClose, onViewExtractedInfo, onFileStateChanged }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [replacePdfBusy, setReplacePdfBusy] = useState(false);
  const [replacePdfReextract, setReplacePdfReextract] = useState(false);
  const [replacePdfNotice, setReplacePdfNotice] = useState("");
  const [replaceExtractedBusy, setReplaceExtractedBusy] = useState(false);
  const [replaceExtractedNotice, setReplaceExtractedNotice] = useState("");
  const [replaceSectionError, setReplaceSectionError] = useState("");
  const [pendingPdfFile, setPendingPdfFile] = useState(null);
  const [pendingJsonFile, setPendingJsonFile] = useState(null);
  const pdfInputRef = useRef(null);
  const jsonInputRef = useRef(null);
  const prevBlobNameRef = useRef(null);

  const anyBusy = actionBusy || replacePdfBusy || replaceExtractedBusy;

  useEffect(() => {
    if (!file) { setPreviewUrl(null); prevBlobNameRef.current = null; return; }
    // A different blob (a genuinely new file selected) resets everything
    // below; the SAME blob (e.g. after a successful PDF replace, which
    // re-passes this file to force a preview refresh) must NOT wipe the
    // success/error text the action just set.
    const isNewFile = prevBlobNameRef.current !== file.blob_name;
    prevBlobNameRef.current = file.blob_name;
    setPreviewUrl(null);
    setLoadingUrl(true);
    setError("");
    if (isNewFile) {
      setActionError("");
      setReplaceSectionError("");
      setReplacePdfNotice("");
      setReplaceExtractedNotice("");
      setReplacePdfReextract(false);
    }
    axios.get(`${API}/api/client-facing/preview-url`, {
      headers: authHeaders(), params: { blob_name: file.blob_name },
    })
      .then((r) => setPreviewUrl(r.data.url))
      .catch((err) => setError(extractErrorDetail(err, "Could not load preview.")))
      .finally(() => setLoadingUrl(false));
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetract = async () => {
    setActionBusy(true); setActionError("");
    try {
      await axios.post(`${API}/api/client-facing/retract`, null, {
        headers: authHeaders(), params: { blob_name: file.blob_name },
      });
      onFileStateChanged({ ...file, retracted: true });
    } catch (err) {
      setActionError(extractErrorDetail(err, "Could not retract this report."));
    }
    setActionBusy(false);
  };

  const handleRestore = async () => {
    setActionBusy(true); setActionError("");
    try {
      await axios.post(`${API}/api/client-facing/restore`, null, {
        headers: authHeaders(), params: { blob_name: file.blob_name },
      });
      onFileStateChanged({ ...file, retracted: false });
    } catch (err) {
      setActionError(extractErrorDetail(err, "Could not restore this report."));
    }
    setActionBusy(false);
  };

  const handleReplacePdfFileChosen = (e) => {
    const chosen = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-choosing the same file next time
    if (!chosen) return;
    setReplaceSectionError("");
    setPendingPdfFile(chosen); // confirmed via ConfirmDialog below — this overwrite has no undo
  };

  const confirmReplacePdf = async () => {
    const chosen = pendingPdfFile;
    if (!chosen) return;
    setReplacePdfBusy(true); setReplaceSectionError(""); setReplacePdfNotice("");
    try {
      const formData = new FormData();
      formData.append("pdf", chosen);
      const resp = await axios.post(`${API}/api/client-facing/replace-pdf`, formData, {
        headers: authHeaders(),
        params: { blob_name: file.blob_name, reextract: replacePdfReextract },
      });
      setReplacePdfNotice(resp.data.reextracting
        ? "PDF replaced — re-extraction has been kicked off in the background. Check \"View Extracted Info\" again in a minute."
        : "PDF replaced. Extracted summary/stats were left untouched.");
      setPendingPdfFile(null);
      onFileStateChanged({ ...file }); // same blob_name — refreshes the preview URL only, notice above is preserved
    } catch (err) {
      setPendingPdfFile(null);
      setReplaceSectionError(extractErrorDetail(err, "Could not replace this report's PDF."));
    }
    setReplacePdfBusy(false);
  };

  const handleReplaceExtractedFileChosen = (e) => {
    const chosen = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!chosen) return;
    setReplaceSectionError("");
    setPendingJsonFile(chosen);
  };

  const confirmReplaceExtracted = async () => {
    const chosen = pendingJsonFile;
    if (!chosen) return;
    setReplaceExtractedBusy(true); setReplaceSectionError(""); setReplaceExtractedNotice("");
    try {
      const formData = new FormData();
      formData.append("json_file", chosen);
      await axios.post(`${API}/api/client-facing/replace-extracted`, formData, {
        headers: authHeaders(), params: { blob_name: file.blob_name },
      });
      setReplaceExtractedNotice("Extracted summary/stats replaced. The PDF itself was not touched.");
      setPendingJsonFile(null);
    } catch (err) {
      setPendingJsonFile(null);
      setReplaceSectionError(extractErrorDetail(err, "Could not replace this report's extracted data."));
    }
    setReplaceExtractedBusy(false);
  };

  const handlePermanentDelete = async () => {
    setActionBusy(true); setActionError("");
    try {
      await axios.delete(`${API}/api/client-facing/permanent-delete`, {
        headers: authHeaders(), params: { blob_name: file.blob_name, confirm: true },
      });
      setConfirmDeleteOpen(false);
      onFileStateChanged(null); // the file is gone — close the preview
    } catch (err) {
      setConfirmDeleteOpen(false);
      setActionError(extractErrorDetail(err, "Could not permanently delete this report."));
    }
    setActionBusy(false);
  };

  if (!file) {
    return (
      <Box sx={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        bgcolor: "rgba(255,255,255,0.02)", color: "rgba(255,255,255,0.25)", gap: 2,
      }}>
        <PictureAsPdf sx={{ fontSize: 48 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Select a file to preview it</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "rgba(255,255,255,0.02)" }}>
      <Box sx={{
        px: 2.5, py: 2, borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={file.filename} enterDelay={400}>
            <Typography sx={{
              fontSize: 14.5, fontWeight: 700, color: "#fff",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {file.filename}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
            {formatBytes(file.size_bytes)} · {file.updated ? formatEST(file.updated) : "—"}
            {file.retracted && <span style={{ color: "#f87171", fontWeight: 700 }}> · Retracted</span>}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0, alignItems: "center" }}>
          <Button size="small" variant="outlined" startIcon={<Insights sx={{ fontSize: 16 }} />}
            onClick={() => onViewExtractedInfo(file)}
            sx={{
              fontSize: 12.5, fontWeight: 700, textTransform: "none", borderRadius: "8px",
              borderColor: "rgba(201,169,110,0.4)", color: B.gold, px: 1.5, py: 0.4,
              "&:hover": { borderColor: B.gold, bgcolor: "rgba(201,169,110,0.08)" },
            }}>
            View Extracted Info
          </Button>
          {previewUrl && (
            <Tooltip title="Download">
              <IconButton size="small" component="a" href={previewUrl} download target="_blank"
                sx={{ color: "rgba(255,255,255,0.6)" }}>
                <Download sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close preview">
            <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {!file.retracted ? (
          <Button size="small" variant="outlined" disabled={anyBusy} onClick={handleRetract}
            startIcon={actionBusy ? <CircularProgress size={13} sx={{ color: "#f87171" }} /> : <Block sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: 12.5, fontWeight: 700, textTransform: "none", borderRadius: "8px",
              borderColor: "rgba(239,68,68,0.4)", color: "#f87171", px: 1.5, py: 0.4,
              "&:hover": { borderColor: "#ef4444", bgcolor: "rgba(239,68,68,0.08)" },
            }}>
            Retract — Wrong Recipient
          </Button>
        ) : (
          <>
            <Button size="small" variant="outlined" disabled={anyBusy} onClick={handleRestore}
              sx={{
                fontSize: 12.5, fontWeight: 700, textTransform: "none", borderRadius: "8px",
                borderColor: "rgba(52,211,153,0.4)", color: "#34d399", px: 1.5, py: 0.4,
                "&:hover": { borderColor: "#34d399", bgcolor: "rgba(52,211,153,0.08)" },
              }}>
              Restore
            </Button>
            <Button size="small" variant="outlined" disabled={anyBusy} onClick={() => setConfirmDeleteOpen(true)}
              sx={{
                fontSize: 12.5, fontWeight: 700, textTransform: "none", borderRadius: "8px",
                borderColor: "rgba(239,68,68,0.5)", color: "#ef4444", px: 1.5, py: 0.4,
                "&:hover": { borderColor: "#ef4444", bgcolor: "rgba(239,68,68,0.1)" },
              }}>
              Permanently Delete
            </Button>
          </>
        )}
        {actionError && (
          <Typography sx={{ fontSize: 12, color: "#f87171", fontWeight: 600 }}>{actionError}</Typography>
        )}
      </Box>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", color: "rgba(255,255,255,0.4)" }}>
          CONTENT CORRECTION
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleReplacePdfFileChosen} />
          <Button size="small" variant="outlined" disabled={anyBusy} onClick={() => pdfInputRef.current?.click()}
            startIcon={replacePdfBusy ? <CircularProgress size={13} sx={{ color: B.gold }} /> : <UploadFile sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: 12.5, fontWeight: 700, textTransform: "none", borderRadius: "8px",
              borderColor: "rgba(201,169,110,0.4)", color: B.gold, px: 1.5, py: 0.4,
              "&:hover": { borderColor: B.gold, bgcolor: "rgba(201,169,110,0.08)" },
            }}>
            {replacePdfReextract ? "Replace PDF + Re-extract" : "Replace PDF Only"}
          </Button>
          <FormControlLabel
            control={<Checkbox size="small" checked={replacePdfReextract} disabled={anyBusy}
              onChange={(e) => setReplacePdfReextract(e.target.checked)}
              icon={<Autorenew sx={{ fontSize: 16, color: "rgba(255,255,255,0.35)" }} />}
              checkedIcon={<Autorenew sx={{ fontSize: 16, color: B.gold }} />} />}
            label="Also re-extract AI summary/stats from the new PDF (Proove reports only)"
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: "rgba(255,255,255,0.55)" } }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <input ref={jsonInputRef} type="file" accept="application/json,.json" hidden onChange={handleReplaceExtractedFileChosen} />
          <Button size="small" variant="outlined" disabled={anyBusy} onClick={() => jsonInputRef.current?.click()}
            startIcon={replaceExtractedBusy ? <CircularProgress size={13} sx={{ color: B.gold }} /> : <UploadFile sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: 12.5, fontWeight: 700, textTransform: "none", borderRadius: "8px",
              borderColor: "rgba(201,169,110,0.4)", color: B.gold, px: 1.5, py: 0.4,
              "&:hover": { borderColor: B.gold, bgcolor: "rgba(201,169,110,0.08)" },
            }}>
            Replace Extracted Data (JSON)
          </Button>
          <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.35)" }}>
            Overwrites the AI summary/stats directly — no OCR, PDF untouched.
          </Typography>
        </Box>
        {replaceSectionError && (
          <Typography sx={{ fontSize: 12, color: "#f87171", fontWeight: 600 }}>{replaceSectionError}</Typography>
        )}
        {replacePdfNotice && (
          <Typography sx={{ fontSize: 12, color: "#34d399", fontWeight: 600 }}>{replacePdfNotice}</Typography>
        )}
        {replaceExtractedNotice && (
          <Typography sx={{ fontSize: 12, color: "#34d399", fontWeight: 600 }}>{replaceExtractedNotice}</Typography>
        )}
      </Box>
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Permanently delete this report?"
        message="This removes the PDF and its extracted AI summary/stats entirely. This cannot be undone — if you're not certain, use Restore instead."
        confirmLabel="Permanently Delete"
        destructive
        loading={actionBusy}
        onConfirm={handlePermanentDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
      <ConfirmDialog
        open={!!pendingPdfFile}
        title="Replace this report's PDF?"
        message={pendingPdfFile
          ? `This swaps the document on this report with "${pendingPdfFile.name}" (${formatBytes(pendingPdfFile.size)}). ${
              replacePdfReextract
                ? "AI summary/stats will also be re-run from the new PDF."
                : "The stored AI summary/stats will NOT be touched."
            } There is no undo for this — double-check this is the right file before continuing.`
          : ""}
        confirmLabel={replacePdfReextract ? "Replace PDF + Re-extract" : "Replace PDF Only"}
        loading={replacePdfBusy}
        onConfirm={confirmReplacePdf}
        onCancel={() => setPendingPdfFile(null)}
      />
      <ConfirmDialog
        open={!!pendingJsonFile}
        title="Replace this report's extracted data?"
        message={pendingJsonFile
          ? `This overwrites the stored AI summary/stats with the contents of "${pendingJsonFile.name}" (${formatBytes(pendingJsonFile.size)}). The PDF itself will NOT be touched. There is no undo for this — double-check this is the right file before continuing.`
          : ""}
        confirmLabel="Replace Extracted Data"
        loading={replaceExtractedBusy}
        onConfirm={confirmReplaceExtracted}
        onCancel={() => setPendingJsonFile(null)}
      />
      <Box sx={{ flex: 1, position: "relative" }}>
        {loadingUrl ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={22} sx={{ color: B.gold }} />
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Typography sx={{ fontSize: 13.5, color: "#f87171" }}>{error}</Typography>
          </Box>
        ) : (
          <iframe src={previewUrl} title={file.filename} style={{ width: "100%", height: "100%", border: "none" }} />
        )}
      </Box>
    </Box>
  );
}

// ─── Explorer ────────────────────────────────────────────────────────────────
function Explorer({ root, subPrefix, onNavigateInto, onFileSelect, selectedBlob, reloadKey }) {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/api/client-facing/browse`, {
        headers: authHeaders(), params: { root, sub_prefix: subPrefix },
      });
      setEntries(res.data);
    } catch (e) {
      setError(extractErrorDetail(e, "Failed to load."));
    }
    setLoading(false);
  }, [root, subPrefix]);

  useEffect(() => { load(); }, [load, reloadKey]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}><CircularProgress size={22} sx={{ color: B.gold }} /></Box>;
  }
  if (error) {
    return <Typography sx={{ fontSize: 13.5, color: "#f87171", textAlign: "center", pt: 5 }}>{error}</Typography>;
  }
  if (entries && entries.folders.length === 0 && entries.files.length === 0) {
    return (
      <Box sx={{ textAlign: "center", pt: 6 }}>
        <CloudDone sx={{ fontSize: 36, color: "rgba(255,255,255,0.12)", mb: 1 }} />
        <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.25)" }}>This folder is empty.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 1 }}>
      {(entries?.folders || []).map((f) => (
        <FolderTile key={f} name={f} onClick={() => onNavigateInto(f)} />
      ))}
      {(entries?.files || []).map((file) => (
        <FileTile key={file.blob_name} file={file}
          selected={selectedBlob === file.blob_name} onClick={() => onFileSelect(file)} />
      ))}
    </Box>
  );
}

// ─── Extracted Info dialog (build item 4) ──────────────────────────────────
// Shows the AI-generated summary + extracted stats/structured data stored
// in MongoDB for the selected PDF — previously invisible anywhere. Patient
// vs. clinician content is never conflated: the backend lookup is an exact
// blob-name match, so this always shows the extraction for THIS specific
// file, in THIS specific tree.
function ExtractedSection({ icon, title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        {icon}
        <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", color: "rgba(255,255,255,0.55)" }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

// 2026-10-26 fix (ui-ux-checker finding, real GCS-simulated UI pass) —
// EXTRACTED STATS used to be a raw JSON.stringify dump in a <pre> block
// — every other section on this page is polished; this one looked like
// debug output left in, exactly the "looks odd" risk flagged directly.
// Deliberately generic (no per-report-type field knowledge) since this
// admin QC view can show ANY stats_json shape — Proove-typed
// (proove_ori/genes/self_reported_pain/etc.) or a native/OCR type this
// file has never heard of (tox_panel, blood_panel, …) — a renderer that
// assumed specific keys would silently fall back to raw JSON for any
// shape it didn't recognize, the opposite of robust. Humanizes keys,
// renders a {score,level,range}-shaped value as a readable one-liner,
// an array of plain values as a comma list, and an array of objects
// (gene findings, risk-management rows, etc.) as a small readable list
// — never a second raw-JSON fallback.
function _humanizeStatsKey(key) {
  return String(key).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function _formatStatsValue(value) {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (value.every((v) => v == null || typeof v !== "object")) return value.join(", ");
    // Array of objects (gene findings, risk-management rows, history
    // questions, …) — render each as its own readable line rather than
    // a second raw-JSON fallback, whatever its keys turn out to be.
    return (
      <Box component="span" sx={{ display: "block" }}>
        {value.map((item, i) => (
          <Box key={i} sx={{ mb: i < value.length - 1 ? 0.5 : 0 }}>
            {Object.entries(item || {})
              .filter(([, v]) => v != null && v !== "")
              .map(([k, v]) => `${_humanizeStatsKey(k)}: ${v}`)
              .join(" · ") || "—"}
          </Box>
        ))}
      </Box>
    );
  }
  if (typeof value === "object") {
    const parts = Object.entries(value)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => (k === "score" || k === "level" ? String(v) : `${_humanizeStatsKey(k)}: ${v}`));
    return parts.length ? parts.join(" · ") : "—";
  }
  return String(value);
}

function FormattedStats({ stats }) {
  const entries = Object.entries(stats || {}).filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0));
  if (!entries.length) {
    return <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>No extracted stats for this report.</Typography>;
  }
  return (
    <Box sx={{
      bgcolor: "rgba(255,255,255,0.04)", borderRadius: "10px", p: 1.75,
    }}>
      {entries.map(([key, value], i) => (
        <Box key={key} sx={{ display: "flex", gap: 1.5, py: 0.9, borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.5)", minWidth: 170, flexShrink: 0 }}>
            {_humanizeStatsKey(key)}
          </Typography>
          <Typography component="div" sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", lineHeight: 1.6 }}>
            {_formatStatsValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function ExtractedInfoDialog({ file, onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!file) return;
    setInfo(null); setLoading(true); setError("");
    axios.get(`${API}/api/client-facing/extracted-info`, {
      headers: authHeaders(), params: { blob_name: file.blob_name },
    })
      .then((r) => setInfo(r.data))
      .catch((err) => setError(extractErrorDetail(err, "Could not load extracted info.")))
      .finally(() => setLoading(false));
  }, [file]);

  return (
    <Dialog open={!!file} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: "#0b1120", borderRadius: "18px", border: "1px solid rgba(255,255,255,0.1)" } }}>
      <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Insights sx={{ color: B.gold, fontSize: 22 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>Extracted Info</Typography>
          {info?.found && (
            <Chip size="small" label={info.report_kind === "patient" ? "Patient-facing extraction" : "Clinician-facing extraction"}
              sx={{
                height: 22, fontWeight: 700, fontSize: 11.5,
                bgcolor: info.report_kind === "patient" ? "rgba(96,165,250,0.15)" : "rgba(52,211,153,0.15)",
                color: info.report_kind === "patient" ? "#60a5fa" : "#34d399",
                border: `1px solid ${info.report_kind === "patient" ? "rgba(96,165,250,0.35)" : "rgba(52,211,153,0.35)"}`,
              }} />
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}>
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={22} sx={{ color: B.gold }} />
          </Box>
        ) : error ? (
          <Typography sx={{ fontSize: 13.5, color: "#f87171", textAlign: "center", py: 3 }}>{error}</Typography>
        ) : info && !info.found ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "rgba(255,255,255,0.7)", mb: 1 }}>
              No extracted data found for this exact file
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.7 }}>
              This usually means the report was re-uploaded since this file was generated — the extraction
              is now linked to the newer version instead, not lost.
            </Typography>
          </Box>
        ) : info ? (
          <>
            {info.retracted && (
              <Chip icon={<Block sx={{ fontSize: 14 }} />} label="This report is currently retracted" size="small" sx={{
                mb: 2.5, bgcolor: "rgba(239,68,68,0.12)", color: "#f87171",
                border: "1px solid rgba(239,68,68,0.3)", fontWeight: 700,
              }} />
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
              {info.report_type_id && <Chip size="small" label={info.report_type_id} sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }} />}
              {info.accession && <Chip size="small" label={`Accession ${info.accession}`} sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }} />}
              {info.stats_extraction_status && <Chip size="small" label={`Extraction: ${info.stats_extraction_status}`} sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }} />}
            </Box>

            {info.ai_summary && (
              <ExtractedSection icon={<MenuBook sx={{ fontSize: 17, color: "rgba(255,255,255,0.5)" }} />} title="AI SUMMARY">
                <Box sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.8)", lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: info.ai_summary }} />
              </ExtractedSection>
            )}

            {(info.stats_json || info.structured_results) && (
              <ExtractedSection icon={<Insights sx={{ fontSize: 17, color: "rgba(255,255,255,0.5)" }} />} title="EXTRACTED STATS">
                <FormattedStats stats={info.stats_json || info.structured_results} />
              </ExtractedSection>
            )}

            {Array.isArray(info.abnormal_value_insights) && info.abnormal_value_insights.length > 0 && (
              <ExtractedSection icon={<WarningAmber sx={{ fontSize: 17, color: "#f0b429" }} />} title="FLAGGED VALUES">
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {info.abnormal_value_insights.map((item, i) => (
                    <Box key={i} sx={{ p: 1.5, borderRadius: "8px", bgcolor: "rgba(240,180,41,0.08)", border: "1px solid rgba(240,180,41,0.2)" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f0b429" }}>
                        {item.value} {item.unit} ({item.flag_type})
                      </Typography>
                      {item.what_it_means_for_you && (
                        <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.65)", mt: 0.5 }}>
                          {item.what_it_means_for_you}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </ExtractedSection>
            )}

            {Array.isArray(info.citations) && info.citations.length > 0 && (
              <ExtractedSection icon={<LibraryBooks sx={{ fontSize: 17, color: "rgba(255,255,255,0.5)" }} />} title="CITATIONS">
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {info.citations.map((c, i) => (
                    <Typography key={i} sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.6)" }}>
                      {typeof c === "string" ? c : JSON.stringify(c)}
                    </Typography>
                  ))}
                </Box>
              </ExtractedSection>
            )}

            {!info.ai_summary && !info.stats_json && !info.structured_results && (
              <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)", textAlign: "center", py: 2 }}>
                A row exists for this file, but no summary/stats have been generated yet.
              </Typography>
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function ClientFacingDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [root, setRoot] = useState(null); // null | "patient" | "clinician"
  const [subPrefix, setSubPrefix] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractedInfoFile, setExtractedInfoFile] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // 2026-10-27 addition (user-directed — "the admin should also have the
  // ability to delete folders also, not just the PDF") — bulk-retracts
  // every live report under the CURRENTLY BROWSED folder, via the new
  // POST /api/client-facing/retract-folder. Reversible, same as a
  // single-file retract (not a permanent delete) — the folder genuinely
  // empties (real GCS folders only exist as long as ≥1 blob sits under
  // them), and reappears if every file in it is later restored.
  const [confirmFolderRetractOpen, setConfirmFolderRetractOpen] = useState(false);
  const [folderRetractBusy, setFolderRetractBusy] = useState(false);
  const [folderRetractError, setFolderRetractError] = useState("");

  // 2026-10-26 fix — subPrefix used to start as "" (the basePrefix
  // fallback was only ever applied at the DISPLAY/API-call layer via
  // `subPrefix || basePrefix`, never written back into state). Seeding
  // it with the real base prefix here keeps the root load itself always
  // correct.
  const enterRoot = (r) => {
    setRoot(r);
    setSubPrefix(r === "patient" ? "patient_reports/" : "clinician_reports/");
    setSelectedFile(null);
  };
  const backToLanding = () => { setRoot(null); setSubPrefix(""); setSelectedFile(null); };

  // 2026-10-26 fix (confirmed real bug, live-reproduced — "Invalid path
  // for this root" on every folder click) — an earlier pass this session
  // "fixed" this by REPLACING subPrefix with the clicked folder value
  // outright, on the premise that the backend returns absolute paths.
  // That premise was wrong: the real backend route
  // (browse_client_facing_gcs, main.py) explicitly strips the queried
  // prefix server-side before responding —
  // `raw_folders = [f[len(prefix):] for f in (iterator.prefixes or [])]`
  // — specifically so the frontend always receives a bare RELATIVE
  // fragment (e.g. "akhil_kumar_.../"), never the full path. Replacing
  // subPrefix with that bare fragment drops the "patient_reports/"/
  // "clinician_reports/" root entirely, so the very next browse call
  // sends a sub_prefix that no longer starts with base_prefix — which is
  // exactly the 403 "Invalid path for this root" check. Fixed by
  // appending the relative fragment to the current subPrefix instead
  // (matching navigateToCrumb's own basePrefix-relative recomputation
  // just below, for the same reason).
  const navigateInto = (folderRelative) => {
    const base = root === "patient" ? "patient_reports/" : "clinician_reports/";
    setSubPrefix((prev) => (prev || base) + folderRelative);
    setSelectedFile(null);
  };
  const navigateToCrumb = (i) => {
    const basePrefix = root === "patient" ? "patient_reports/" : "clinician_reports/";
    const relative = subPrefix.slice(basePrefix.length).split("/").filter(Boolean);
    const newRelative = relative.slice(0, i + 1).join("/") + "/";
    setSubPrefix(basePrefix + newRelative);
    setSelectedFile(null);
  };

  const basePrefix = root === "patient" ? "patient_reports/" : "clinician_reports/";
  const parts = subPrefix ? subPrefix.slice(basePrefix.length).split("/").filter(Boolean).map((p) => p + "/") : [];
  const currentFolder = subPrefix || basePrefix;
  // Matches the backend's own validation — cannot bulk-retract an entire
  // root (every patient or every clinician report site-wide) at once,
  // only a folder actually drilled into.
  const canRetractCurrentFolder = currentFolder !== basePrefix;

  const handleRetractFolder = async () => {
    setFolderRetractBusy(true);
    setFolderRetractError("");
    try {
      await axios.post(`${API}/api/client-facing/retract-folder`, null, {
        headers: authHeaders(), params: { root, sub_prefix: currentFolder },
      });
      setSelectedFile(null);
      setReloadKey((k) => k + 1);
      setConfirmFolderRetractOpen(false);
    } catch (err) {
      setFolderRetractError(extractErrorDetail(err, "Could not retract this folder."));
    }
    setFolderRetractBusy(false);
  };

  return (
    <AdminPageShell
      isDark={isDark}
      title="Client-Facing Verification"
      subtitle={root
        ? `Browsing the real ${root === "patient" ? "patient" : "clinician"}-facing report tree — exactly what a ${root} sees`
        : "Browse the exact GCS folder tree a clinician or patient sees, to verify a report landed in the right place"}
      backPath="/admin"
      backLabel="Back to Admin"
      onLogout={() => { localStorage.removeItem("admin_token"); navigate("/admin-login"); }}
      actions={root && (
        <Button variant="outlined" size="small" onClick={backToLanding}
          sx={{ borderColor: B.navy, color: isDark ? "#fff" : B.navy, textTransform: "none", fontWeight: 600 }}>
          Choose Different Tree
        </Button>
      )}
    >
      <Box sx={{ bgcolor: "#0b1120", borderRadius: "18px", border: "1px solid rgba(255,255,255,0.08)",
        minHeight: 560, overflow: "hidden" }}>
        <AnimatePresence mode="wait">
          {!root ? (
            // 2026-10-26 fix (ui-ux-checker finding, real UI sweep) — the
            // two root-choice cards used to sit top-aligned inside this
            // panel's full 560px minHeight with only top padding, leaving
            // a large empty band below and reading as unfinished rather
            // than a deliberate layout. Centering vertically fills the
            // panel intentionally instead.
            <motion.div key="landing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 560 }}>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 4, p: 8, flexWrap: "wrap" }}>
                <RootCard
                  icon={<LocalHospital sx={{ fontSize: 32, color: B.gold }} />}
                  title="Clinicians"
                  description="Browse by practice → clinician, exactly as the clinician portal sees their own report folders."
                  onClick={() => enterRoot("clinician")}
                />
                <RootCard
                  icon={<PersonOutline sx={{ fontSize: 32, color: B.gold }} />}
                  title="Patients"
                  description="Browse by patient identity folder, exactly as the patient portal sees their own reports."
                  onClick={() => enterRoot("patient")}
                />
              </Box>
            </motion.div>
          ) : (
            <motion.div key="explorer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ display: "flex", height: 560 }}>
              <Box sx={{ width: selectedFile ? "46%" : "100%", minWidth: 420, display: "flex", flexDirection: "column",
                borderRight: selectedFile ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid rgba(255,255,255,0.06)",
                  display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Breadcrumbs parts={parts} onNavigateRoot={backToLanding} onNavigate={navigateToCrumb} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {canRetractCurrentFolder && (
                      <Tooltip title="Retract every report in this folder — reversible, the folder empties for the patient/clinician until restored">
                        <Button size="small" variant="outlined" onClick={() => setConfirmFolderRetractOpen(true)}
                          sx={{ borderColor: "rgba(239,68,68,0.4)", color: "#f87171", textTransform: "none",
                                fontWeight: 600, fontSize: 12, py: 0.25, minWidth: 0 }}>
                          Retract Folder
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Refresh">
                      <IconButton size="small" onClick={() => setReloadKey((k) => k + 1)} sx={{ color: "rgba(255,255,255,0.5)" }}>
                        <Refresh sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {folderRetractError && (
                  <Typography sx={{ fontSize: 12, color: "#f87171", px: 2.5, pt: 1 }}>{folderRetractError}</Typography>
                )}
                <ConfirmDialog
                  open={confirmFolderRetractOpen}
                  title="Retract every report in this folder?"
                  message="This retracts every currently-live report under this folder — the folder will disappear for the patient/clinician until individually restored. This is reversible, not a permanent delete."
                  confirmLabel="Retract Folder"
                  destructive
                  loading={folderRetractBusy}
                  onConfirm={handleRetractFolder}
                  onCancel={() => setConfirmFolderRetractOpen(false)}
                />
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  <Explorer root={root} subPrefix={subPrefix || basePrefix} onNavigateInto={navigateInto}
                    onFileSelect={setSelectedFile} selectedBlob={selectedFile?.blob_name} reloadKey={reloadKey} />
                </Box>
              </Box>
              {selectedFile && (
                <PreviewPanel file={selectedFile} onClose={() => setSelectedFile(null)}
                  onViewExtractedInfo={setExtractedInfoFile}
                  onFileStateChanged={(updatedFile) => {
                    setSelectedFile(updatedFile);
                    setReloadKey((k) => k + 1); // refresh the explorer's own retracted badge
                  }} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
      <ExtractedInfoDialog file={extractedInfoFile} onClose={() => setExtractedInfoFile(null)} />
    </AdminPageShell>
  );
}
